import { SelectOption } from '@missionlabs/react'
import {
    NumberCheckCapability,
    OutboundMessageType,
    OutboundWhatsApp,
    PostWhatsAppSendMessageRequest,
    WhatsAppBusinessTemplate,
    WhatsAppContentType
} from '@missionlabs/types'
import {
    createContext,
    Dispatch,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef
} from 'react'
import { usePostNumberCheckMutation, usePostWhatsAppMessageMutation } from 'shared/store'

import { useGetMyWhatsAppNumbers } from './useGetMyWhatsAppNumbers'
import { useWhatsAppTemplates } from './useWhatsAppTemplates'

type IWhatsAppSendMessageContext = {
    dispatch: Dispatch<MessageAction>
    draftMessage: OutboundWhatsApp
    templates: WhatsAppBusinessTemplate[]
    template: WhatsAppBusinessTemplate | undefined
    templateOptions: SelectOption[]
    myWhatsAppNumberOptions: SelectOption[]
    send: (text?: string) => Promise<void>
    isSending: boolean
    sendError: string | null
}

type MessageAction =
    | { type: 'SET_MESSAGE'; draftMessage: Partial<OutboundWhatsApp> }
    | { type: 'RESET_MESSAGE' }

export const initialMessageState: OutboundWhatsApp = {
    contentType: WhatsAppContentType.TEMPLATE,
    type: OutboundMessageType.WhatsApp,
    numberID: '',
    toNumber: '',
    templateName: '',
    text: '',
    attachment: undefined
}

export function messageReducer(state: OutboundWhatsApp, action: MessageAction) {
    switch (action.type) {
        case 'SET_MESSAGE': {
            // Return the same state when nothing changes, so no-op dispatches don't re-render
            const hasChanges = Object.entries(action.draftMessage).some(
                ([key, value]) => state[key] !== value
            )
            return hasChanges ? { ...state, ...action.draftMessage } : state
        }
        case 'RESET_MESSAGE':
            return initialMessageState
        default:
            return state
    }
}

const WhatsAppSendMessageContext = createContext<IWhatsAppSendMessageContext | null>(null)

export const WhatsAppSendMessageProvider = ({ children }: PropsWithChildren) => {
    const [draftMessage, dispatch] = useReducer(messageReducer, initialMessageState)
    const [sendWhatsApp, { isLoading: isSending, error: sendError }] =
        usePostWhatsAppMessageMutation()
    const [numberCheck] = usePostNumberCheckMutation()
    const { myWhatsAppNumberOptions, myWhatsAppNumbers } = useGetMyWhatsAppNumbers()
    const { templates, templateOptions } = useWhatsAppTemplates()
    // from/to pair the capability check last ran for
    const checkedFromAndTo = useRef<string | null>(null)

    const { numberID, templateName, contentType, toNumber } = draftMessage

    const template = useMemo(() => {
        return templates.find(t => t.name === templateName)
    }, [templateName, templates])

    const fromNumberE164 = useMemo(
        () => myWhatsAppNumbers.find(number => number.ID === numberID)?.numberE164,
        [myWhatsAppNumbers, numberID]
    )

    // Default to the first number and, for template messages, the first template
    useEffect(() => {
        const defaults: Partial<OutboundWhatsApp> = {}
        if (!numberID && myWhatsAppNumberOptions.length > 0) {
            defaults.numberID = myWhatsAppNumberOptions[0].value
        }
        if (
            !templateName &&
            contentType === WhatsAppContentType.TEMPLATE &&
            templateOptions.length > 0
        ) {
            defaults.templateName = templateOptions[0].value
        }
        if (Object.keys(defaults).length > 0) {
            dispatch({ type: 'SET_MESSAGE', draftMessage: defaults })
        }
    }, [myWhatsAppNumberOptions, numberID, templateOptions, templateName, contentType])

    // We need to check if we can send a text message or a template message using the numberCheck API
    const checkNumberCapability = useCallback(
        async (fromNumber: string, to: string) => {
            const fromAndTo = `${fromNumber}-${to}`
            checkedFromAndTo.current = fromAndTo
            try {
                const response = await numberCheck({ fromNumber, toNumbers: [to] }).unwrap()
                // Ignore stale responses if from/to changed while the request was in flight
                if (checkedFromAndTo.current !== fromAndTo || !response.length) return

                const { capabilities = [] } = response[0]
                const canSendText = capabilities.includes(NumberCheckCapability.WHATSAPP_TEXT)
                dispatch({
                    type: 'SET_MESSAGE',
                    draftMessage: canSendText
                        ? { contentType: WhatsAppContentType.TEXT, templateName: '' }
                        : { contentType: WhatsAppContentType.TEMPLATE }
                })
            } catch {
                // Keep the current content type if the check fails
            }
        },
        [numberCheck]
    )

    useEffect(() => {
        if (!fromNumberE164 || !toNumber) return
        if (checkedFromAndTo.current === `${fromNumberE164}-${toNumber}`) return
        checkNumberCapability(fromNumberE164, toNumber)
    }, [fromNumberE164, toNumber, checkNumberCapability])

    const send = useCallback(
        async (overrideText?: string) => {
            const { numberID, toNumber, contentType, templateName, attachment } = draftMessage
            const text = overrideText ?? draftMessage.text

            const isTemplate = contentType === WhatsAppContentType.TEMPLATE
            const hasAttachment = !!attachment

            if (!numberID || !toNumber) throw new Error('Missing numberID or toNumber')
            if (isTemplate && !templateName) throw new Error('Missing templateName')
            if (!isTemplate && !text && !hasAttachment) {
                throw new Error('Missing text or attachment')
            }

            const payloadText = isTemplate ? undefined : { body: text ?? '' }
            const payloadTemplate = !isTemplate
                ? undefined
                : {
                      name: templateName ?? '',
                      language: { code: template?.language || 'en_GB' }
                  }

            const payload: PostWhatsAppSendMessageRequest & { numberID: string } = {
                to: toNumber,
                type: contentType,
                numberID,
                text: payloadText,
                template: payloadTemplate,
                attachment: hasAttachment ? attachment : undefined
            }

            await sendWhatsApp(payload).unwrap()

            dispatch({
                type: 'SET_MESSAGE',
                draftMessage: { templateName: '', text: '', attachment: undefined }
            })

            // Sending doesn't necessarily open the 24h window (e.g. a template message),
            // so ask the API again instead of assuming free text is allowed now
            if (fromNumberE164) checkNumberCapability(fromNumberE164, toNumber)
        },
        [draftMessage, sendWhatsApp, template, fromNumberE164, checkNumberCapability]
    )

    // Memoize the context value to avoid unnecessary re-renders
    const value = useMemo(
        () => ({
            dispatch,
            draftMessage,
            templateOptions,
            templates,
            template,
            myWhatsAppNumberOptions,
            send,
            isSending,
            sendError: sendError ? JSON.stringify(sendError) : null
        }),
        [
            draftMessage,
            templateOptions,
            templates,
            template,
            myWhatsAppNumberOptions,
            send,
            isSending,
            sendError
        ]
    )

    return (
        <WhatsAppSendMessageContext.Provider value={value}>
            {children}
        </WhatsAppSendMessageContext.Provider>
    )
}

export const useWhatsAppSendMessage = () => {
    const ctx = useContext(WhatsAppSendMessageContext)
    if (!ctx) {
        throw Error('useWhatsAppSendMessage used outside of <WhatsAppSendMessageContextProvider />')
    }
    return ctx
}
